import {
  Box,
  Flex,
  Skeleton,
  useToast,
  Editable,
  EditableInput,
  EditablePreview,
  useEditableControls,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useState, useEffect, useMemo, useCallback } from 'react';

import { LimitedPartnerAliases } from './LimitedPartnerAliases';
import { LimitedPartnerCapitalTrendsGraphContainer } from './LimitedPartnerCapitalTrendsGraph';
import { LimitedPartnerDetailsCommitmentsTableContainer } from './LimitedPartnerDetailsCommitmentsTable';
import { LimitedPartnerDetailSummaryCards } from './LimitedPartnerDetailSummaryCards';
import { colors } from 'packages/common-frontend/colors';
import { Attribute } from 'packages/common-frontend/components/AttributeInput/types';
import {
  mapAttributeToGQLValue,
  mapGqlAttributeToAttribute,
} from 'packages/common-frontend/components/AttributeInput/utils';
import { IconButtonV2 } from 'packages/common-frontend/components/IconButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import { AttributesEditor } from 'packages/limitedPartners/ui/AttributesEditor';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { SingleEntityDrawer as LimitedPartnerEventLogSingleEntityDrawer } from '~/core/routes/limited-partners/EntityEventLogDrawers';
import { Breadcrumbs } from '~/packages/common-frontend/components/Breadcrumbs';
import {
  dashboardRoutePadding,
  errorToastConfig,
} from '~/packages/common-frontend/constants';
import {
  useGetLimitedPartnerDetailsQuery,
  useUpdateLimitedPartnerMutation,
  useGetCommitmentsForLimitedPartnerQuery,
  GetLimitedPartnerDetailsDocumentGQLType,
  useCreateOrUpdateLimitedPartnerAttributeValueMutation,
} from '~/packages/limitedPartners/requests/requests.gql.generated';

const LimitedPartnerEditableNameBody = ({
  limitedPartnerName,
}: {
  limitedPartnerName: string;
}) => {
  const {
    isEditing,
    getEditButtonProps,
    getSubmitButtonProps,
  } = useEditableControls();

  return (
    <Flex alignItems="center" gap="1rem">
      <EditablePreview />
      <EditableInput pl="0.5rem" />
      {isEditing ? (
        <IconButtonV2
          {...getSubmitButtonProps()}
          aria-label="Save"
          colorScheme="blue"
          icon={<Icon icon="save" />}
          variant="v2-ghost"
          placement="right"
          isDisabled={limitedPartnerName.trim() === ''}
        />
      ) : (
        <IconButtonV2
          {...getEditButtonProps()}
          aria-label="Edit"
          colorScheme="gray"
          icon={<Icon icon="edit" />}
          variant="v2-ghost"
          placement="right"
        />
      )}
    </Flex>
  );
};

const LimitedPartnerEditableName = ({
  initialName,
  updateName,
}: {
  initialName: string;
  updateName: (limitedPartnerName: string) => void;
}) => {
  const [limitedPartnerName, setLimitedPartnerName] = useState(initialName);

  useEffect(() => {
    setLimitedPartnerName(initialName);
  }, [initialName]);

  return (
    <Box flexShrink="0">
      <Editable
        display="flex"
        value={limitedPartnerName}
        onChange={(newValue) => setLimitedPartnerName(newValue)}
        onSubmit={() => {
          updateName(limitedPartnerName.trim());
        }}
        placeholder="Enter name"
        style={{
          color: limitedPartnerName ? 'black' : colors.gray[300],
          fontSize: '2.25rem',
          fontWeight: 'bold',
          lineHeight: '2.6875rem',
        }}
        submitOnBlur={false}
      >
        <LimitedPartnerEditableNameBody
          limitedPartnerName={limitedPartnerName}
        />
      </Editable>
    </Box>
  );
};

export const LimitedPartnerDetails = ({
  limitedPartnerId,
}: {
  limitedPartnerId: string;
}) => {
  const toast = useToast();
  const { user } = useUserAuthOrThrow();

  const {
    data: limitedPartnerDetails,
    error: limitedPartnerDetailsError,
  } = useGetLimitedPartnerDetailsQuery({
    variables: {
      limitedPartnerId,
      organizationId: user.organizationId,
    },
  });

  const attributes = useMemo(() => {
    const mappedAttributes =
      limitedPartnerDetails?.attributes.map((attr) =>
        mapGqlAttributeToAttribute(attr),
      ) ?? [];

    // sort by displayName for a deterministic order
    return mappedAttributes.sort((a, b) => {
      return a.displayName.localeCompare(b.displayName);
    });
  }, [limitedPartnerDetails]);

  const [updateLimitedPartnerMutation] = useUpdateLimitedPartnerMutation({
    onError: (err) => {
      errorToastConfig.title = err.message;
      toast(errorToastConfig);
    },
    refetchQueries: [GetLimitedPartnerDetailsDocumentGQLType],
  });

  const [
    createOrUpdateAttributeValue,
  ] = useCreateOrUpdateLimitedPartnerAttributeValueMutation({
    refetchQueries: [GetLimitedPartnerDetailsDocumentGQLType],
  });

  const onUpdateAttribute = useCallback(
    async (updatedAttribute: Attribute) => {
      try {
        await createOrUpdateAttributeValue({
          variables: {
            input: {
              entityDomain: 'limitedPartner',
              entityIdentifier: limitedPartnerId,
              attributeId: updatedAttribute.attributeId,
              ...mapAttributeToGQLValue(updatedAttribute),
            },
          },
        });
      } catch (err) {
        Sentry.captureException(err);
        toast({
          ...errorToastConfig,
          title: `Failed to update ${updatedAttribute.displayName} attribute.`,
        });
      }
    },
    [createOrUpdateAttributeValue, toast, limitedPartnerId],
  );

  const [refetchingCommitments, setRefetchingCommitments] = useState(false);

  const {
    data: limitedPartnerCommitments,
    error: limitedPartnerCommitmentsError,
    refetch: refetchLimitedPartnerCommitments,
  } = useGetCommitmentsForLimitedPartnerQuery({
    variables: {
      limitedPartnerId,
    },
  });

  if (limitedPartnerDetailsError) throw limitedPartnerDetailsError;
  if (limitedPartnerCommitmentsError) throw limitedPartnerCommitmentsError;
  return (
    <Box padding={dashboardRoutePadding}>
      <Breadcrumbs
        breadcrumbs={[{ label: 'Limited Partners', path: `/limited-partners` }]}
      />
      {limitedPartnerDetails ? (
        <>
          <Flex
            alignItems="flex-start"
            flexWrap="wrap"
            justifyContent="space-between"
            marginTop="0.5rem"
          >
            <LimitedPartnerEditableName
              initialName={limitedPartnerDetails.limitedPartner.name}
              updateName={(limitedPartnerName: string) => {
                updateLimitedPartnerMutation({
                  variables: {
                    limitedPartnerId,
                    name: limitedPartnerName,
                  },
                });
              }}
            />
            <LimitedPartnerEventLogSingleEntityDrawer
              entityId={limitedPartnerId}
            />
          </Flex>

          <Flex direction="column" gap="1rem" mt="2.5rem">
            <Typography variant="section-subtitle" trim>
              Names
            </Typography>
            <LimitedPartnerAliases
              initialPrimaryName={limitedPartnerDetails.limitedPartner.name}
              initialAliases={
                limitedPartnerDetails.limitedPartner.alternateAliases
              }
              updateNames={(primaryName: string, aliases: string[]) => {
                updateLimitedPartnerMutation({
                  variables: {
                    limitedPartnerId,
                    name: primaryName,
                    alternateAliases: aliases,
                  },
                });
              }}
            />
          </Flex>

          <Flex direction="column" gap="1rem" mt="2.5rem">
            <Typography variant="section-subtitle" trim>
              Summary
            </Typography>
            <LimitedPartnerDetailSummaryCards
              commitmentData={limitedPartnerCommitments}
              refetchingCommitments={refetchingCommitments}
            />
          </Flex>

          <Flex direction="column" gap="1rem" mt="2.5rem">
            <Typography variant="section-subtitle" trim>
              Attributes
            </Typography>
            <AttributesEditor
              attributes={attributes}
              updateAttribute={onUpdateAttribute}
            />
          </Flex>

          <LimitedPartnerCapitalTrendsGraphContainer
            limitedPartnerId={limitedPartnerId}
          />
          <LimitedPartnerDetailsCommitmentsTableContainer
            commitmentData={limitedPartnerCommitments}
            refetchingCommitments={refetchingCommitments}
            setRefetchingCommitments={setRefetchingCommitments}
            refetchLimitedPartnerCommitments={async () => {
              await refetchLimitedPartnerCommitments();
            }}
          />
        </>
      ) : (
        <Skeleton height="75vh" mt="2.5rem" />
      )}
    </Box>
  );
};
